"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Card, CardBody, CardHeader, Row, Col, Badge, Spinner, Button } from "reactstrap"
import { FaArrowLeft, FaCalendar, FaUsers } from "react-icons/fa"
import { BadgeRequestService } from "../../../Service/BadgeRequestService"
import type { ExhibitorVerification, Participation } from "./badge-request"
import { toast } from "react-toastify"

interface Props {
  exhibitor: ExhibitorVerification
  onParticipationSelect: (participation: Participation) => void
  onBack: () => void
}

const ParticipationsList: React.FC<Props> = ({ exhibitor, onParticipationSelect, onBack }) => {
  const [participations, setParticipations] = useState<Participation[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchParticipations()
  }, [exhibitor.exhibitor])

  const fetchParticipations = async () => {
    try {
      setLoading(true)
      const data = await BadgeRequestService.getExhibitorParticipations(exhibitor.exhibitor)
      setParticipations(data)
    } catch (error) {
      console.error("Failed to fetch participations:", error)
      toast.error("Failed to load participations")
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <Spinner color="primary" />
      </div>
    )
  }

  return (
    <div>
      <Card>
        <CardHeader>
          <div className="d-flex align-items-center">
            <Button color="secondary" size="sm" onClick={onBack} className="me-3">
              <FaArrowLeft size={12} />
            </Button>
            <div>
              <h5 className="mb-0">Participations - {exhibitor.exhibitor_name}</h5>
              <small className="text-muted">Select a participation to request badges for</small>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {participations.length === 0 ? (
            <div className="text-center py-5">
              <h6>No Active Participations</h6>
              <p className="text-muted">This company has no active participations.</p>
            </div>
          ) : (
            <Row>
              {participations.map((participation) => (
                <Col md={6} lg={4} key={participation.id} className="mb-3">
                  <Card
                    className="h-100 cursor-pointer border-hover"
                    onClick={() => onParticipationSelect(participation)}
                    style={{ cursor: "pointer", transition: "all 0.2s" }}
                  >
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="mb-0">{participation.exhibition_title}</h6>
                        <Badge color={participation.status ? "success" : "secondary"} pill>
                          {participation.status ? "Active" : "Inactive"}
                        </Badge>
                      </div>

                      <div className="text-muted small">
                        <div className="d-flex align-items-center mb-1">
                          <FaUsers className="me-2" size={12} />
                          {participation.stand_category_name}
                        </div>
                        <div className="d-flex align-items-center mb-1">
                          <FaCalendar className="me-2" size={12} />
                          Max Participants: {participation.num_representatives}
                        </div>
                        <div className="text-xs">
                          Created: {new Date(participation.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

export default ParticipationsList
